import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { APP_CONFIG } from '@app/shared/config/app.config';
import { IAccount } from '@app/shared/models/banking.model';
import { IConcentratorAccount, IPurchaseRequest } from '@app/shared/models/bouncer.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { AccountBasicService } from '@app/shared/services/banking/account-basic.service';
import { BouncerService } from '@app/shared/services/banking/bouncer.service';
import { AccountContext } from '@app/shared/services/context/account.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { STATUS } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { PurchaseReason } from '@app/shared/types/enums/bouncer';
import { catchError, EMPTY, filter, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';

@Component({
  selector: 'app-withdraw',
  templateUrl: './withdraw.component.html',
  styleUrls: ['./withdraw.component.sass'],
})
export class WithdrawComponent extends SubscriberComponent implements OnInit, OnDestroy {
  form = new FormGroup({});
  isCancellationAccount!: boolean;
  reasonOptions = [PurchaseReason.CANCEL_ACCOUNT, PurchaseReason.LIMIT, PurchaseReason.ERROR];
  concentratorAccounts$: Observable<IConcentratorAccount[] | undefined> = EMPTY;
  account: IAccount | null = null;
  STATUS = STATUS;
  initialState!: IDynamicDialogConfig;
  balance = 0;
  hasNotBalance = false;

  constructor(
    private readonly _accountCtx: AccountContext,
    private readonly modalSvc: ModalService,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _accountBasicSvc: AccountBasicService,
    private readonly _bouncerSvc: BouncerService,
    private readonly _authSvc: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.account = this._accountCtx.getCurrentAccount();
    this.concentratorAccounts$ = this._bouncerSvc.getConcentratorAccounts(APP_CONFIG.dispersion.key);
    this.initForm();
    this.getmodalData();
    this.logAmplitudeEvent();
    this.confirmActionSubscription();
    if (this.account?.status !== STATUS.CLOSED) {
      this.getBalance();
    }
  }

  private getBalance() {
    const rppUserId = this._customerCtxSvc.getRppUserId();

    this._subscriptions.sink = this._bouncerSvc.getAccountBalance(this.account?.code || '').subscribe(res => {
      this.balance = res?.available_balance || 0;
      this.hasNotBalance = this.balance === 0;
      this.setFormValidations();
    });
  }

  private setFormValidations() {
    // add max validation for max amount to withdraw
    this.form.get('amount')?.addValidators([Validators.max(this.balance)]);

    //disable fields when balance is 0
    if (this.hasNotBalance) {
      this.form.get('concentratorAccount')?.disable();
      this.form.get('reason')?.disable();
      this.form.get('amount')?.disable();
    }
  }

  private logAmplitudeEvent() {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.WITHDRAW,
    });
  }

  private initForm() {
    this.form = new FormGroup({
      amount: new FormControl('', [Validators.required, Validators.min(0)]),
      concentratorAccount: new FormControl('', [Validators.required]),
      reason: new FormControl('', [Validators.required]),
    });

    this._subscriptions.sink = this.form.get('reason')?.valueChanges.subscribe(value => {
      if (value === PurchaseReason.CANCEL_ACCOUNT) {
        this.form.get('amount')?.setValue(this.balance);
        this.form.get('amount')?.disable();
      } else {
        this.form.get('amount')?.setValue('');
        this.form.get('amount')?.enable();
      }
    });

    //Change the state of the confirmn button (disbale or not) inside modal
    this._subscriptions.sink = this.form.valueChanges.subscribe(value => {
      const data = {
        ...this.initialState,
        modalComponentData: {
          ...this.initialState.modalComponentData,
          acceptButtonTitleDisabled: this.form.invalid || this.hasNotBalance,
        },
      };

      this.modalSvc.setModalData(data);
    });
  }

  private confirmActionSubscription() {
    this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.destroy$),
        filter(value => value),
        switchMap(() => this._authSvc.user$),
        switchMap(agent => {
          const rppUserId = this._customerCtxSvc.getRppUserId();
          const { amount, reason } = this.form.value;
          const request: IPurchaseRequest = {
            amount: Number(amount),
            originTransaction: this.form.get('concentratorAccount')?.value,
            reason,
            agentEmail: agent?.email || '',
          };

          return this._bouncerSvc.makePurchase(rppUserId, this.account?.code || '', request).pipe(
            catchError(error => {
              this.modalSvc.closeWithErrorMsg(this.initialState.modalComponentData.modalName);

              return EMPTY;
            })
          );
        }),
        tap(res => {
          this.modalSvc.closeWithSuccessMsg(this.initialState.modalComponentData.modalName);

          //only for update Account UI
          this._accountCtx.accountCodeChange(this.account?.code);
        })
      )
      .subscribe();
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => (this.initialState = data),
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }
}
